import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { EmptyState, KpiCard, MetaStrip, SectionCard } from '../../components/admin/AdminPrimitives';
import { formatCurrencyINRCompact, formatCurrencyINRFull, formatPercent } from '../../utils/adminFormat';
import { getTrips, isApiError } from '../../services/dashboardService';
import {
  getAdminApprovalSlaReport,
  getAdminApprovalSlaBreaches,
  getAdminDepartmentSpendReport,
  getAdminGstReportDownloadUrl,
  getAdminReimbursementRiskReport,
  getExpenseEvents,
  isExpenseApiError,
  subscribeControlTowerStream,
} from '../../services/expenseService';

type CategorySpend = {
  category: string;
  amount: number;
  percent: number;
};

type BottleneckManager = {
  manager: string;
  overdueCount: number;
  avgOverdueHours: number;
  atRiskAmount: number;
};

type PaymentMethodInsight = {
  method: string;
  count: number;
  total: number;
  avgTicket: number;
};

type ComplianceTrendPoint = {
  bucket: string;
  total: number;
  compliant: number;
  rate: number;
};

type DestinationAnalytics = {
  destination: string;
  tripCount: number;
  totalBudget: number;
  avgLeadDays: number;
};

type LeadershipAction = {
  title: string;
  detail: string;
  owner: 'Finance' | 'Ops' | 'Leadership';
  tone: 'neutral' | 'warning' | 'danger' | 'positive';
};

const AdminAnalyticsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [categorySpend, setCategorySpend] = useState<CategorySpend[]>([]);
  const [paymentMethodInsights, setPaymentMethodInsights] = useState<PaymentMethodInsight[]>([]);
  const [complianceTrend, setComplianceTrend] = useState<ComplianceTrendPoint[]>([]);
  const [destinationAnalytics, setDestinationAnalytics] = useState<DestinationAnalytics[]>([]);
  const [leadershipActions, setLeadershipActions] = useState<LeadershipAction[]>([]);
  const [approvedRate, setApprovedRate] = useState(0);
  const [totalTrips, setTotalTrips] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [travelBudgetTotal, setTravelBudgetTotal] = useState(0);
  const [avgExpense, setAvgExpense] = useState(0);
  const [slaOverdue, setSlaOverdue] = useState(0);
  const [avgApprovalHours, setAvgApprovalHours] = useState(0);
  const [slaCompliancePct, setSlaCompliancePct] = useState(100);
  const [riskAmount, setRiskAmount] = useState(0);
  const [bottleneckManagers, setBottleneckManagers] = useState<BottleneckManager[]>([]);
  const [crossDepartmentSplitRate, setCrossDepartmentSplitRate] = useState(0);
  const [splitCoveragePct, setSplitCoveragePct] = useState(0);
  const [pendingLiability, setPendingLiability] = useState(0);
  const [liveConnected, setLiveConnected] = useState(false);
  const [lastPushAt, setLastPushAt] = useState<Date | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    const load = async (silent = false) => {
      try {
        if (!silent) {
          setIsLoading(true);
          setErrorMessage(null);
        }

        const [trips, events, slaReport, slaBreaches, riskReport, departmentReport] = await Promise.all([
          getTrips({ perPage: 500 }),
          getExpenseEvents({ perPage: 1000 }),
          getAdminApprovalSlaReport(),
          getAdminApprovalSlaBreaches({ perPage: 500 }),
          getAdminReimbursementRiskReport(),
          getAdminDepartmentSpendReport(),
        ]);
        setTotalTrips(trips.length);
        setTotalEvents(events.length);
        setSlaOverdue(slaReport.overdue || 0);
        setAvgApprovalHours(Number(slaReport.avg_response_hours || 0));
        setSlaCompliancePct(Math.max(0, Math.min(100, Number(slaReport.completion_rate || 0))));
        setRiskAmount(Number(riskReport.at_risk_amount || 0));

        const splits = events.flatMap((event) =>
          (event.splits || []).map((split) => ({
            amount: Number(split.split_amount || 0),
            status: String(split.status || '').toLowerCase(),
            category: event.category || 'Uncategorized',
          }))
        );

        const splitEvents = events.filter((event) => (event.splits || []).length > 0);
        const multiDeptEvents = events.filter((event) => {
          const departments = new Set(
            (event.splits || [])
              .map((split) => String(split.department || '').trim().toLowerCase())
              .filter(Boolean)
          );
          return departments.size > 1;
        });
        const pendingSplitAmount = splits
          .filter((item) => item.status === 'pending')
          .reduce((sum, item) => sum + item.amount, 0);
        setPendingLiability(pendingSplitAmount);
        setCrossDepartmentSplitRate(events.length > 0 ? (multiDeptEvents.length / events.length) * 100 : 0);
        setSplitCoveragePct(events.length > 0 ? (splitEvents.length / events.length) * 100 : 0);

        const bottleneckMap = new Map<string, { overdueCount: number; overdueHours: number; atRiskAmount: number }>();
        slaBreaches.forEach((row) => {
          const manager = row.manager_name || 'Unassigned manager';
          const existing = bottleneckMap.get(manager) || { overdueCount: 0, overdueHours: 0, atRiskAmount: 0 };
          existing.overdueCount += 1;
          existing.overdueHours += Number(row.overdue_hours || 0);
          existing.atRiskAmount += Number(row.split_amount || 0);
          bottleneckMap.set(manager, existing);
        });
        const bottlenecks = Array.from(bottleneckMap.entries())
          .map(([manager, value]) => ({
            manager,
            overdueCount: value.overdueCount,
            avgOverdueHours: value.overdueCount > 0 ? value.overdueHours / value.overdueCount : 0,
            atRiskAmount: value.atRiskAmount,
          }))
          .sort((a, b) => {
            if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount;
            return b.avgOverdueHours - a.avgOverdueHours;
          })
          .slice(0, 5);
        setBottleneckManagers(bottlenecks);

        const totalExpenseAmount = splits.reduce((sum, item) => sum + item.amount, 0);
        const reportTotal = departmentReport.reduce((sum, item) => sum + Number(item.total_amount || 0), 0);
        const reportCount = departmentReport.reduce((sum, item) => sum + Number(item.split_count || 0), 0);
        setAvgExpense(reportCount > 0 ? reportTotal / reportCount : (splits.length > 0 ? totalExpenseAmount / splits.length : 0));

        const approved = splits.filter((item) => ['approved', 'auto_approved'].includes(item.status)).length;
        const localApprovedRate = splits.length > 0 ? (approved / splits.length) * 100 : 0;
        setApprovedRate(slaReport.total > 0 ? Number(slaReport.completion_rate || 0) : localApprovedRate);

        const byCategory = new Map<string, number>();
        splits.forEach((item) => {
          byCategory.set(item.category, (byCategory.get(item.category) || 0) + Number(item.amount || 0));
        });

        const sorted = Array.from(byCategory.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6);

        const topTotal = sorted.reduce((sum, [, amount]) => sum + amount, 0) || 1;
        setCategorySpend(sorted.map(([category, amount]) => ({ category, amount, percent: Math.round((amount / topTotal) * 100) })));

        const payments = events.flatMap((event) => event.payments || []);
        setTotalPayments(payments.length);
        const paymentByMethod = new Map<string, { count: number; total: number }>();
        payments.forEach((payment) => {
          const method = String(payment.payment_method || 'Unknown').trim() || 'Unknown';
          const existing = paymentByMethod.get(method) || { count: 0, total: 0 };
          existing.count += 1;
          existing.total += Number(payment.amount_paid || 0);
          paymentByMethod.set(method, existing);
        });
        const methodInsights = Array.from(paymentByMethod.entries())
          .map(([method, value]) => ({
            method,
            count: value.count,
            total: value.total,
            avgTicket: value.count > 0 ? value.total / value.count : 0,
          }))
          .sort((a, b) => b.total - a.total);
        setPaymentMethodInsights(methodInsights);

        const complianceMap = new Map<string, { total: number; compliant: number }>();
        events.forEach((event) => {
          const created = new Date(event.created_at || '');
          if (Number.isNaN(created.getTime())) return;
          const bucket = created.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          const hasPolicyFlags = Boolean(event.trust_signals?.flagged) || (event.trust_signals?.signals?.length || 0) > 0;
          const isRejected = String(event.aggregate_status || '').toUpperCase() === 'REJECTED';
          const compliant = !hasPolicyFlags && !isRejected;
          const existing = complianceMap.get(bucket) || { total: 0, compliant: 0 };
          existing.total += 1;
          if (compliant) existing.compliant += 1;
          complianceMap.set(bucket, existing);
        });
        const complianceSeries = Array.from(complianceMap.entries())
          .map(([bucket, value]) => ({
            bucket,
            total: value.total,
            compliant: value.compliant,
            rate: value.total > 0 ? (value.compliant / value.total) * 100 : 0,
          }))
          .slice(-6);
        setComplianceTrend(complianceSeries);

        const destinationMap = new Map<string, { tripCount: number; totalBudget: number; leadDaysTotal: number; leadSamples: number }>();
        let totalBudget = 0;
        trips.forEach((trip) => {
          const destination = String(trip.destination || 'Unspecified');
          const existing = destinationMap.get(destination) || { tripCount: 0, totalBudget: 0, leadDaysTotal: 0, leadSamples: 0 };
          const budget = Number(trip.estimated_budget || 0);
          totalBudget += budget;
          existing.tripCount += 1;
          existing.totalBudget += budget;
          const createdAt = new Date(trip.created_at || '');
          const startAt = new Date(trip.start_date || '');
          if (!Number.isNaN(createdAt.getTime()) && !Number.isNaN(startAt.getTime())) {
            const leadDays = Math.max(0, Math.round((startAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)));
            existing.leadDaysTotal += leadDays;
            existing.leadSamples += 1;
          }
          destinationMap.set(destination, existing);
        });
        setTravelBudgetTotal(totalBudget);
        const destinationRows = Array.from(destinationMap.entries())
          .map(([destination, value]) => ({
            destination,
            tripCount: value.tripCount,
            totalBudget: value.totalBudget,
            avgLeadDays: value.leadSamples > 0 ? value.leadDaysTotal / value.leadSamples : 0,
          }))
          .sort((a, b) => b.totalBudget - a.totalBudget)
          .slice(0, 6);
        setDestinationAnalytics(destinationRows);

        const actions: LeadershipAction[] = [];
        if ((slaReport.overdue || 0) > 0) {
          actions.push({
            title: 'Resolve approval backlog',
            detail: `${slaReport.overdue} overdue approvals are breaching SLA.`,
            owner: 'Ops',
            tone: 'danger',
          });
        }
        if (Number(riskReport.at_risk_amount || 0) > 0) {
          actions.push({
            title: 'Review reimbursement exposure',
            detail: `${formatCurrencyINRFull(Number(riskReport.at_risk_amount || 0))} is currently at risk.`,
            owner: 'Finance',
            tone: 'warning',
          });
        }
        if (events.length > 0 && (multiDeptEvents.length / events.length) > 0.35) {
          actions.push({
            title: 'Audit cross-department cost sharing',
            detail: `${Math.round((multiDeptEvents.length / events.length) * 100)}% events span multiple departments.`,
            owner: 'Leadership',
            tone: 'neutral',
          });
        }
        if (actions.length === 0) {
          actions.push({
            title: 'Operational health is stable',
            detail: 'No immediate risks detected in current cycle analytics.',
            owner: 'Leadership',
            tone: 'positive',
          });
        }
        setLeadershipActions(actions.slice(0, 3));
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (!silent) {
          if (isExpenseApiError(error)) setErrorMessage(error.message);
          else if (isApiError(error)) setErrorMessage(error.message);
          else if (error instanceof Error) setErrorMessage(error.message);
          else setErrorMessage('Failed to load analytics.');
        }
      } finally {
        if (!silent) setIsLoading(false);
      }
    };

    void load();
    const refreshInterval = window.setInterval(() => {
      void load(true);
    }, 60000);
    const unsubscribe = subscribeControlTowerStream({
      onConnected: () => setLiveConnected(true),
      onError: () => setLiveConnected(false),
      onEvent: () => {
        setLastPushAt(new Date());
        void load(true);
      },
    });

    return () => {
      window.clearInterval(refreshInterval);
      unsubscribe();
    };
  }, []);

  const monthlyProjection = useMemo(() => categorySpend.reduce((sum, item) => sum + item.amount, 0), [categorySpend]);
  const gstCsvUrl = getAdminGstReportDownloadUrl('csv');
  const gstExcelUrl = getAdminGstReportDownloadUrl('excel');

  return (
    <AdminShell
      pageTitle="Analytics"
      subtitle="Performance indicators and spending patterns across the enterprise."
    >
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <MetaStrip
            left="Reporting period: current control cycle"
            right={`Live: ${liveConnected ? 'connected' : 'disconnected'} • Last refreshed: ${lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}`}
          />

          <SectionCard title="GST Layer Exports" subtitle="GST-compliant ledger view for tax credit and filing workflows.">
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={gstCsvUrl}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Export GST CSV
              </a>
              <a
                href={gstExcelUrl}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Export GST Excel
              </a>
            </div>
          </SectionCard>

          <SectionCard title="Real-Time Expense Insights Dashboard" subtitle="Continuously refreshed operations view for spend, risk, and throughput.">
            <div className="grid gap-3 md:grid-cols-4">
              <KpiCard label="Expense Events" value={String(totalEvents)} helper="Tracked in current cycle" tone="neutral" />
              <KpiCard label="Payment Transactions" value={String(totalPayments)} helper="Captured payment records" tone="neutral" />
              <KpiCard label="Pending Liability" value={formatCurrencyINRFull(pendingLiability)} helper="Awaiting approval action" tone={pendingLiability > 0 ? 'warning' : 'positive'} />
              <KpiCard
                label="Live Push Status"
                value={liveConnected ? 'Connected' : 'Polling'}
                helper={lastPushAt ? `Last event ${lastPushAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : 'No push events yet'}
                tone={liveConnected ? 'positive' : 'warning'}
              />
            </div>
          </SectionCard>

          <SectionCard title="SLA Command Metrics" subtitle="This is the operator-grade proof of speed and control.">
            <div className="grid gap-3 md:grid-cols-4">
              <KpiCard label="Avg Approval Time" value={`${avgApprovalHours.toFixed(1)}h`} helper="Mean response time for approval decisions" tone={avgApprovalHours > 24 ? 'danger' : avgApprovalHours > 12 ? 'warning' : 'positive'} />
              <KpiCard label="% Within SLA" value={formatPercent(slaCompliancePct)} helper={`${slaOverdue} approvals are currently overdue`} tone={slaCompliancePct < 90 ? 'warning' : 'positive'} />
              <KpiCard label="Approval Throughput" value={formatPercent(approvedRate)} helper="Share of splits approved/auto-approved" tone={approvedRate < 90 ? 'warning' : 'positive'} />
              <KpiCard label="At-Risk Reimbursement Amount" value={formatCurrencyINRFull(riskAmount)} helper="Current cycle high-risk exposure" tone={riskAmount > 0 ? 'danger' : 'positive'} />
            </div>
          </SectionCard>

          <SectionCard title="Top Bottleneck Managers" subtitle="Managers with the heaviest SLA drag right now.">
            {bottleneckManagers.length === 0 ? (
              <EmptyState compact message="No current bottlenecks. All manager queues are within SLA." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Manager</th>
                      <th className="pb-2 text-right">Overdue approvals</th>
                      <th className="pb-2 text-right">Avg overdue</th>
                      <th className="pb-2 text-right">At-risk amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bottleneckManagers.map((row) => (
                      <tr key={row.manager} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-3 font-semibold text-slate-900">{row.manager}</td>
                        <td className="py-3 text-right">{row.overdueCount}</td>
                        <td className="py-3 text-right">{row.avgOverdueHours.toFixed(1)}h</td>
                        <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.atRiskAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Split Intelligence (Signature Capability)"
            subtitle="Cross-department allocation + split-level approvals + live liability distribution."
          >
            <div className="grid gap-3 md:grid-cols-3">
              <KpiCard label="Cross-Department Allocation Rate" value={formatPercent(crossDepartmentSplitRate)} helper="Expense events split across 2+ departments" tone={crossDepartmentSplitRate > 25 ? 'positive' : 'neutral'} />
              <KpiCard label="Split-Level Approval Coverage" value={formatPercent(splitCoveragePct)} helper="Events processed with split-level governance" tone={splitCoveragePct > 80 ? 'positive' : 'warning'} />
              <KpiCard label="Live Pending Liability" value={formatCurrencyINRFull(pendingLiability)} helper="Pending split amount waiting for approval" tone={pendingLiability > 0 ? 'warning' : 'positive'} />
            </div>
          </SectionCard>

          <SectionCard title="Travel Spend Analytics & Reporting" subtitle="Trip-level spend posture and destination concentration for travel programs.">
            <div className="mb-3 grid gap-3 md:grid-cols-3">
              <KpiCard label="Total Trips" value={String(totalTrips)} helper="Current reporting cycle" />
              <KpiCard label="Travel Budget Booked" value={formatCurrencyINRFull(travelBudgetTotal)} helper="From trip estimated budgets" />
              <KpiCard label="Avg Claim Value" value={formatCurrencyINRFull(avgExpense)} helper="Across all split claims" />
            </div>
            {destinationAnalytics.length === 0 ? (
              <EmptyState compact message="No travel booking data available for destination analytics." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Destination</th>
                      <th className="pb-2 text-right">Trips</th>
                      <th className="pb-2 text-right">Booked budget</th>
                      <th className="pb-2 text-right">Avg booking lead time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinationAnalytics.map((row) => (
                      <tr key={row.destination} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-3 font-semibold text-slate-900">{row.destination}</td>
                        <td className="py-3 text-right">{row.tripCount}</td>
                        <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.totalBudget)}</td>
                        <td className="py-3 text-right">{row.avgLeadDays.toFixed(1)} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Payment Data Analysis" subtitle="Transaction mix, ticket size, and payment method concentration.">
            {paymentMethodInsights.length === 0 ? (
              <EmptyState compact message="No payment rows available for current period." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Payment method</th>
                      <th className="pb-2 text-right">Transactions</th>
                      <th className="pb-2 text-right">Total paid</th>
                      <th className="pb-2 text-right">Avg ticket size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentMethodInsights.map((row) => (
                      <tr key={row.method} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-3 font-semibold text-slate-900">{row.method}</td>
                        <td className="py-3 text-right">{row.count}</td>
                        <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.total)}</td>
                        <td className="py-3 text-right">{formatCurrencyINRFull(row.avgTicket)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Policy Compliance Tracking Over Time" subtitle="Monthly compliance trend from policy/trust signals and rejection outcomes.">
            {complianceTrend.length === 0 ? (
              <EmptyState compact message="Not enough historical events to compute compliance trend." />
            ) : (
              <div className="grid gap-3 md:grid-cols-3">
                {complianceTrend.map((point) => (
                  <KpiCard
                    key={point.bucket}
                    label={point.bucket}
                    value={formatPercent(point.rate)}
                    helper={`${point.compliant}/${point.total} events compliant`}
                    tone={point.rate < 90 ? 'warning' : 'positive'}
                    compact
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Actionable Data for Finance & Leadership" subtitle="Priority actions generated from live analytics signals.">
            <div className="grid gap-3 md:grid-cols-3">
              {leadershipActions.map((action) => (
                <KpiCard
                  key={action.title}
                  label={`${action.owner} Action`}
                  value={action.title}
                  helper={action.detail}
                  tone={action.tone}
                />
              ))}
            </div>
          </SectionCard>

          <div className="grid gap-3 md:grid-cols-2">
            <KpiCard label="Average Claim Value" value={formatCurrencyINRFull(avgExpense)} helper={`${totalTrips} trips in current cycle`} />
            <KpiCard label="Open SLA Risk Count" value={String(slaOverdue)} helper="Approval lines currently breaching SLA" tone={slaOverdue > 0 ? 'danger' : 'positive'} />
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
            <SectionCard title="Category Contribution" subtitle="Top spend categories with share of tracked amount.">
              {categorySpend.length === 0 ? (
                <EmptyState message="No categorized expense data for the selected period." />
              ) : (
                <div className="space-y-3">
                  {categorySpend.map((item) => (
                    <div key={item.category}>
                      <div className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-700">
                        <span>{item.category}</span>
                        <span>{formatCurrencyINRFull(item.amount)}</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-slate-200">
                        <div className="h-full rounded-full bg-slate-700" style={{ width: `${item.percent}%` }} title={`${item.percent}% share`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Projected Month-End Spend" subtitle="Based on current run-rate for top categories.">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Projection (Top Categories)</p>
                <p className="mt-2 text-3xl font-extrabold text-slate-900">{formatCurrencyINRFull(monthlyProjection)}</p>
                <p className="mt-2 text-sm font-medium text-slate-600">Formula: category run-rate x remaining cycle days. Axis/report view uses compact labels like {formatCurrencyINRCompact(monthlyProjection)}.</p>
              </div>
            </SectionCard>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminAnalyticsPage;
